import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { authFetch } from '../../../utils/authFetch';

interface WorkspaceDetails {
  id: string;
  name: string;
  type: 'personal' | 'team' | 'enterprise';
  structure: 'personal' | 'team' | 'enterprise';
  purpose: 'personal' | 'business' | 'client';
  status: 'active' | 'closed';
  avatarUrl: string | null;
  onboardingVersion: number;
  createdAt: string;
}

const PURPOSE_LABELS: Record<string, string> = {
  personal: 'Personal Life',
  business: 'Business',
  client: 'Client Services',
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

interface GeneralTabProps {
  workspaceId: string;
  authToken: string;
}

/**
 * General settings tab
 *
 * Features:
 * - Workspace name editing
 * - Avatar upload/remove
 * - Workspace type display
 * - Workspace ID copy
 * - Close workspace / transfer ownership
 */
export function GeneralTab({ workspaceId, authToken }: GeneralTabProps) {
  const [workspace, setWorkspace] = useState<WorkspaceDetails | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editName, setEditName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadWorkspace();
  }, [workspaceId]);

  async function loadWorkspace() {
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}`, authToken, {
        
      });
      if (response.ok) {
        const data = await response.json();
        setWorkspace(data.workspace);
        setEditName(data.workspace.name);
        if (data.membership?.role) {
          setUserRole(data.membership.role);
        }
      }
    } catch {
      toast.error('Failed to load workspace');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSaveName() {
    if (!editName.trim() || editName === workspace?.name) {
      setIsEditingName(false);
      return;
    }

    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}`, authToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ name: editName.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        setWorkspace(data.workspace);
        setIsEditingName(false);
        toast.success('Workspace name updated');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update name');
      }
    } catch {
      toast.error('Failed to update name');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAvatarUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, or WebP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      // Step 1: Get presigned URL
      const presignRes = await authFetch('/api/assets/presign', authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          assetType: 'workspace-avatar',
          entityId: workspaceId,
          mimeType: file.type,
          fileSize: file.size,
        }),
      });

      if (!presignRes.ok) {
        const error = await presignRes.json();
        throw new Error(error.error || 'Failed to get upload URL');
      }

      const { uploadUrl, assetKey, version } = await presignRes.json();

      // Step 2: Upload to S3 (direct S3 URL, no Central auth)
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error('Failed to upload image');
      }

      // Step 3: Commit the upload
      const commitRes = await authFetch('/api/assets/commit', authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          assetType: 'workspace-avatar',
          entityId: workspaceId,
          assetKey,
          version,
        }),
      });

      if (!commitRes.ok) {
        throw new Error('Failed to save avatar');
      }

      const { cdnUrl } = await commitRes.json();
      setWorkspace((prev) => (prev ? { ...prev, avatarUrl: cdnUrl } : null));
      toast.success('Avatar updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload avatar');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  async function handleRemoveAvatar() {
    // Note: This would typically call a DELETE endpoint
    // For now, we'll update with null avatar
    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}`, authToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ avatarUrl: null }),
      });

      if (response.ok) {
        setWorkspace((prev) => (prev ? { ...prev, avatarUrl: null } : null));
        toast.success('Avatar removed');
      }
    } catch {
      toast.error('Failed to remove avatar');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCopyId() {
    navigator.clipboard.writeText(workspaceId);
    toast.success('Workspace ID copied');
  }

  async function handlePurposeChange(newPurpose: string) {
    if (newPurpose === workspace?.purpose) return;

    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}`, authToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ purpose: newPurpose }),
      });

      if (response.ok) {
        const data = await response.json();
        setWorkspace((prev) => prev ? { ...prev, purpose: data.workspace.purpose } : null);
        toast.success('Workspace purpose updated');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update purpose');
      }
    } catch {
      toast.error('Failed to update purpose');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCloseWorkspace() {
    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/close`, authToken, {
        method: 'POST',
        
      });

      if (response.ok) {
        setWorkspace((prev) => (prev ? { ...prev, status: 'closed' } : null));
        setShowCloseConfirm(false);
        toast.success('Workspace closed');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to close workspace');
      }
    } catch {
      toast.error('Failed to close workspace');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleReopenWorkspace() {
    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/reopen`, authToken, {
        method: 'POST',
        
      });

      if (response.ok) {
        setWorkspace((prev) => (prev ? { ...prev, status: 'active' } : null));
        toast.success('Workspace reopened');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to reopen workspace');
      }
    } catch {
      toast.error('Failed to reopen workspace');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleTransferOwnership() {
    if (!transferEmail.trim()) return;

    setIsSaving(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/transfer-ownership`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ newOwnerEmail: transferEmail.trim() }),
      });

      if (response.ok) {
        setShowTransferModal(false);
        setTransferEmail('');
        toast.success('Ownership transferred successfully');
        // Reload to reflect new permissions
        loadWorkspace();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to transfer ownership');
      }
    } catch {
      toast.error('Failed to transfer ownership');
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <p className="text-gray-500">Failed to load workspace</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Workspace Profile */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Workspace Profile</h2>
        </div>
        <div className="p-6 space-y-6">
          {/* Avatar */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Workspace Avatar
            </label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
                {workspace.avatarUrl ? (
                  <img
                    src={workspace.avatarUrl}
                    alt={workspace.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-semibold text-gray-400">
                    {workspace.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </button>
                {workspace.avatarUrl && (
                  <button
                    onClick={handleRemoveAvatar}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-gray-300 rounded-lg hover:bg-red-50"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              PNG, JPEG, or WebP. Max 5MB.
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Workspace Name
            </label>
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Workspace name"
                />
                <button
                  onClick={handleSaveName}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingName(false);
                    setEditName(workspace.name);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-gray-900">{workspace.name}</span>
                <button
                  onClick={() => setIsEditingName(true)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Edit
                </button>
              </div>
            )}
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Workspace Type
            </label>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700 capitalize">
                {workspace.type}
              </span>
            </div>
          </div>

          {/* Purpose */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Workspace Purpose
            </label>
            {userRole === 'owner' ? (
              <select
                value={workspace.purpose ?? 'business'}
                onChange={(e) => handlePurposeChange(e.target.value)}
                disabled={isSaving}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="personal">Personal Life</option>
                <option value="business">Business</option>
                <option value="client">Client Services</option>
              </select>
            ) : (
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                  {PURPOSE_LABELS[workspace.purpose] ?? workspace.purpose}
                </span>
              </div>
            )}
          </div>

          {/* ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Workspace ID
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-600 truncate">
                {workspaceId}
              </code>
              <button
                onClick={handleCopyId}
                className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Workspace Information (Read-only) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Workspace Information</h2>
          <p className="text-sm text-gray-500 mt-1">System details about this workspace</p>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {/* Status */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    workspace.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {workspace.status === 'active' ? 'Active' : 'Closed'}
                </span>
              </dd>
            </div>

            {/* Created Date */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(workspace.createdAt)}</dd>
            </div>

            {/* Onboarding Version */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Configuration Version</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {workspace.onboardingVersion === 2 ? 'v2 (Focus-based)' : 'v1 (Legacy)'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-lg shadow-sm border border-red-200">
        <div className="px-6 py-4 border-b border-red-200 bg-red-50">
          <h2 className="text-lg font-medium text-red-900">Danger Zone</h2>
        </div>
        <div className="p-6 space-y-4">
          {/* Transfer Ownership */}
          <div className="flex items-center justify-between py-3">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Transfer Ownership</h3>
              <p className="text-sm text-gray-500">
                Transfer this workspace to another team member
              </p>
            </div>
            <button
              onClick={() => setShowTransferModal(true)}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50"
            >
              Transfer
            </button>
          </div>

          {/* Close/Reopen Workspace */}
          <div className="flex items-center justify-between py-3 border-t border-gray-100">
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                {workspace.status === 'closed' ? 'Reopen Workspace' : 'Close Workspace'}
              </h3>
              <p className="text-sm text-gray-500">
                {workspace.status === 'closed'
                  ? 'Reactivate this workspace and restore access'
                  : 'Deactivate this workspace and cancel subscriptions'}
              </p>
            </div>
            {workspace.status === 'closed' ? (
              <button
                onClick={handleReopenWorkspace}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isSaving ? 'Reopening...' : 'Reopen'}
              </button>
            ) : (
              <button
                onClick={() => setShowCloseConfirm(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Close Workspace
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Close Confirmation Modal */}
      {showCloseConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Close Workspace?</h3>
            <p className="text-gray-600 mb-6">
              This will deactivate the workspace and cancel any active subscriptions. Team members will lose access.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseWorkspace}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isSaving ? 'Closing...' : 'Yes, Close Workspace'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Transfer Ownership</h3>
            <p className="text-gray-600 mb-4">
              Enter the email of the team member who will become the new owner.
            </p>
            <input
              type="email"
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              placeholder="new-owner@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-6"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setTransferEmail('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleTransferOwnership}
                disabled={isSaving || !transferEmail.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isSaving ? 'Transferring...' : 'Transfer Ownership'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
